/**
 * AddStakeholderDialog — añade un stakeholder del cliente al proyecto.
 *
 * Solo pide email + roles + RACI opcional. El backend hace smart-routing
 * (Doc 12 §"Smart-routing del stakeholder por email"):
 *  1. Email coincide con user activo del tenant → liga directo.
 *  2. Email tiene invite pendiente del tenant → no duplica invite.
 *  3. Email no existe → crea invite (role=client) + project_member en
 *     misma transacción. El JIT lo liga cuando el cliente entre.
 *
 * El response del backend trae flags (`linked_existing_user`,
 * `invite_already_pending`, `invite_created`) — los usamos para mostrar
 * el toast correcto al admin.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../../api/client'
import {
  addProjectMember,
  type RaciRole,
} from '../../../api/project_members'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { pushToast } from '../../../stores/toastStore'
import { RoleChecklist } from './RoleChecklist'
import { isValidEmail, type RoleOption } from './roles'

interface AddStakeholderDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  /** Catálogo filtrado a roles con scope `client_stakeholder` (ya
   * filtrado en `ProjectTeamView` con `filterRolesByScope`). */
  roles: RoleOption[]
}

export function AddStakeholderDialog({
  open,
  onClose,
  projectId,
  roles,
}: AddStakeholderDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const defaultRoles = roles[0] ? [roles[0].template_key] : []

  const [email, setEmail] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<string[]>(defaultRoles)
  const [raci, setRaci] = useState<RaciRole | ''>('A')
  const [subscribeAll, setSubscribeAll] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      if (!isValidEmail(email)) {
        setError(t('team.add_stakeholder_dialog.errors.email_invalid'))
        throw new Error('validation')
      }
      const body = {
        roles: selectedRoles,
        external_email: email.trim(),
        ...(raci ? { raci_role: raci } : {}),
        subscribe_all_events: subscribeAll,
      }
      return addProjectMember(accessToken, projectId, body)
    },
    onSuccess: (response) => {
      // Mensaje según el flag del smart-routing (Doc 12).
      const trimmed = email.trim()
      let toastMessage: string
      if (response.linked_existing_user) {
        toastMessage = t('team.toasts.stakeholder_linked', { name: trimmed })
      } else if (response.invite_already_pending) {
        toastMessage = t('team.toasts.stakeholder_invite_pending', {
          name: trimmed,
        })
      } else if (response.invite_created) {
        toastMessage = t('team.toasts.stakeholder_invite_created', {
          name: trimmed,
        })
      } else {
        // Fallback (path con user_id directo — UI no lo dispara hoy)
        toastMessage = t('team.toasts.added', { name: trimmed })
      }
      pushToast({ kind: 'success', message: toastMessage })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'members'],
      })
      // El invite recién creado afecta /settings/team también
      void queryClient.invalidateQueries({
        queryKey: ['tenant-invites'],
      })
      reset()
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'validation') return
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.add_stakeholder_dialog.errors.submit_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      setError(message)
    },
  })

  function reset() {
    setEmail('')
    setSelectedRoles(defaultRoles)
    setRaci('A')
    setSubscribeAll(false)
    setError(null)
  }

  function handleClose() {
    if (mutation.isPending) return
    reset()
    onClose()
  }

  const submitting = mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('team.add_stakeholder_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('team.remove_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={submitting || !isValidEmail(email) || selectedRoles.length === 0}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? t('team.add_stakeholder_dialog.submitting')
              : t('team.add_stakeholder_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.add_stakeholder_dialog.email_label')}
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError(null)
            }}
            placeholder={t('team.add_stakeholder_dialog.email_placeholder')}
            disabled={submitting}
            autoComplete="off"
            spellCheck={false}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
          />
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('team.add_stakeholder_dialog.email_help')}
          </span>
        </label>

        <div className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.add_stakeholder_dialog.role_label')}
          </span>
          <RoleChecklist
            roles={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            disabled={submitting}
            ariaLabel={t('team.add_stakeholder_dialog.role_label')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">
              {t('team.add_stakeholder_dialog.raci_label')}
            </span>
            <select
              value={raci}
              onChange={(e) => setRaci(e.target.value as RaciRole | '')}
              disabled={submitting}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('team.raci_options.none')}</option>
              <option value="R">{t('team.raci_options.R')}</option>
              <option value="A">{t('team.raci_options.A')}</option>
              <option value="C">{t('team.raci_options.C')}</option>
              <option value="I">{t('team.raci_options.I')}</option>
            </select>
          </label>
        </div>

        <label className="flex items-start gap-2 rounded-md border border-line bg-surface-muted/40 p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={subscribeAll}
            onChange={(e) => setSubscribeAll(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 w-4 h-4 rounded border-line text-brand focus:ring-brand focus:ring-offset-0"
          />
          <span className="text-xs">
            <span className="block font-medium text-ink">
              {t('team.add_stakeholder_dialog.subscribe_all_label')}
            </span>
            <span className="block text-ink-muted mt-0.5">
              {t('team.add_stakeholder_dialog.subscribe_all_help')}
            </span>
          </span>
        </label>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
